import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import NavHeader from "../shared/components/NavHeader";
import DropdownMenu, { MenuItem } from "../shared/components/DropdownMenu";
import EventStore from "../event/store/eventStore";
import SetupStore from "./store/setupStore";
import SetupsHeader from "./components/SetupsHeader";
import RoundSelector from "./components/RoundSelector";
import CompletedSetupsList from "./components/CompletedSetupsList";
import PreparingSetupsList from "./components/PreparingSetupsList";
import SendSetupsButton from "./components/SendSetupsButton";
import UngeneratedSetups from "./components/UngeneratedSetups";
import CommunityNavigationStore, {
  CommunityView,
} from "../navigation/communityNavigationStore";
import { RegenerateSetupsConfirmationContent } from "./components/SetupsPopUp";
import LoadingOverlay from "../shared/components/LoadingOverlay";

interface SetupsPageProps {
  onBack: () => void;
}

const SetupsPageComponent: React.FC<SetupsPageProps> = ({ onBack }) => {
  const handleSendNow = async () => {
    try {
      await SetupStore.acceptRound();
      if (EventStore.sitchEventData?.guid) {
        await EventStore.loadEventData(EventStore.sitchEventData.guid);
      }
    } catch (error) {
      console.error("Failed to send setups:", error);
    }
  };

  const handleRegenerateSetup = () => {
    CommunityNavigationStore.setPopUpView({
      onClose: () => CommunityNavigationStore.setPopUpView(null),
      children: (
        <RegenerateSetupsConfirmationContent
          onRegenerate={async () => {
            CommunityNavigationStore.setPopUpView(null);
            try {
              await SetupStore.regenerateSetup();
            } catch (error) {
              console.error("Failed to regenerate setup:", error);
            }
          }}
        />
      ),
    });
  };

  const handleCreateManualSetups = () => {
    CommunityNavigationStore.navigateTo(CommunityView.MANUAL_MATCH);
  };

  const handleGenerateSetups = async () => {
    SetupStore.isLoading = true;
    try {
      await EventStore.generateSetups();
      if (EventStore.sitchEventData?.guid) {
        await EventStore.loadEventData(EventStore.sitchEventData.guid);
      }
      if (SetupStore.currentRound?.guid) {
        await SetupStore.loadCandidatesForRound(SetupStore.currentRound.guid);
      }
    } catch (err) {
      console.error("Failed to generate setups:", err);
    } finally {
      SetupStore.isLoading = false;
    }
  };

  const menuItems: MenuItem[] = [
    {
      label: "🔄 Regenerate setup",
      onClick: handleRegenerateSetup,
    },
    {
      label: "🤝 Create manual setups",
      onClick: handleCreateManualSetups,
    },
  ];

  useEffect(() => {
    if (SetupStore.currentRound?.guid) {
      SetupStore.loadCandidatesForRound(SetupStore.currentRound.guid);
    }
  }, [SetupStore.currentRound?.guid]);

  useEffect(() => {
    if (EventStore.sitchEventData?.guid) {
      EventStore.loadEventData(EventStore.sitchEventData.guid);
    }
  }, []);

  const showCompleted = SetupStore.currentRound?.is_accepted;
  const showPreparing =
    !SetupStore.currentRound?.is_accepted && SetupStore.candidates.length > 0;
  const showUngenerated =
    !SetupStore.currentRound?.is_accepted && SetupStore.candidates.length === 0;

  return (
    <div className="min-h-screen bg-black text-white relative">
      <div className="relative z-10">
        <NavHeader onBack={onBack} />

        <div className="max-w-md mx-auto px-6 py-8">
          <div className="flex items-baseline justify-between mb-6 relative">
            <SetupsHeader />
            <DropdownMenu menuItems={menuItems} />
          </div>

          {EventStore.sitchEventData?.num_rounds && (
            <RoundSelector numRounds={EventStore.sitchEventData.num_rounds} />
          )}

          {showCompleted && (
            <CompletedSetupsList
              candidates={SetupStore.candidates}
              numRounds={EventStore.sitchEventData?.num_rounds}
              currentRound={SetupStore.activeRound}
            />
          )}

          {showPreparing && (
            <PreparingSetupsList
              candidates={SetupStore.candidates}
              isDisabled={SetupStore.isLoading}
              onDisqualifyClicked={SetupStore.rejectCandidate}
            />
          )}

          {showUngenerated && (
            <UngeneratedSetups
              onStartNewRound={handleGenerateSetups}
              handleCreateManualSetups={handleCreateManualSetups}
            />
          )}

          <SendSetupsButton
            hasSetups={showPreparing && SetupStore.candidates.length > 0}
            onSendNow={handleSendNow}
          />
        </div>
      </div>

      <LoadingOverlay isVisible={SetupStore.isLoading} />
    </div>
  );
};

const SetupsPage = observer(SetupsPageComponent);

export default SetupsPage;
